import { getRemoteConfigNumber } from '../firebase';
import {
  DEFAULT_TRANSPORT_TIMEOUT_MS, GRAPHQL_TRANSPORT_TIMEOUT_MS, LONG_TRANSPORT_TIMEOUT_MS, MIN_TIMEOUT_MS,
  SCORE_SYNC_TIMEOUT_MS, TIMEOUT_REMOTE_CONFIG_KEYS,
} from './timeout.constants';

export type TransportTimeoutTier = 'default' | 'graphql' | 'scoreSync' | 'long';

const TIER_SETTINGS: Record<TransportTimeoutTier, { key: string; fallbackMs: number; minMs: number }> = {
  default: { key: TIMEOUT_REMOTE_CONFIG_KEYS.transportDefaultMs, fallbackMs: DEFAULT_TRANSPORT_TIMEOUT_MS, minMs: MIN_TIMEOUT_MS },
  graphql: { key: TIMEOUT_REMOTE_CONFIG_KEYS.transportGraphqlMs, fallbackMs: GRAPHQL_TRANSPORT_TIMEOUT_MS, minMs: GRAPHQL_TRANSPORT_TIMEOUT_MS },
  scoreSync: { key: TIMEOUT_REMOTE_CONFIG_KEYS.scoreSyncMs, fallbackMs: SCORE_SYNC_TIMEOUT_MS, minMs: MIN_TIMEOUT_MS },
  long: { key: TIMEOUT_REMOTE_CONFIG_KEYS.transportLongMs, fallbackMs: LONG_TRANSPORT_TIMEOUT_MS, minMs: MIN_TIMEOUT_MS },
};

// The one place a network bound's value is resolved: Remote Config, never below the tier's floor.
export const transportTimeoutMs = (tier: TransportTimeoutTier): number => {
  const { key, fallbackMs, minMs } = TIER_SETTINGS[tier];
  return getRemoteConfigNumber(key, fallbackMs, minMs);
};

// A request abandoned because its bound elapsed — never an AbortError, which callers read as a cancel.
export class RequestTimeoutError extends Error {
  constructor(readonly label: string, readonly timeoutMs: number) {
    super(`${label} timed out after ${timeoutMs}ms`);
    this.name = 'RequestTimeoutError';
  }
}

export interface IAbortTimer {
  signal: AbortSignal;
  // Restarts the bound, so a stream that keeps delivering is never cut off mid-answer.
  touch: () => void;
  // A RequestTimeoutError when the bound fired; otherwise the original error, untouched.
  settleError: (error: unknown) => unknown;
  dispose: () => void;
}

// Aborts the request when `timeoutMs` passes without a touch(), or when `upstream` aborts.
export const createAbortTimer = (label: string, timeoutMs: number, upstream?: AbortSignal | null): IAbortTimer => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  const abortFromUpstream = () => controller.abort();
  const touch = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
  };

  if (upstream?.aborted) controller.abort();
  else upstream?.addEventListener('abort', abortFromUpstream);
  touch();

  return {
    signal: controller.signal,
    touch,
    settleError: (error) => {
      if (!timedOut) return error;
      console.warn(`[transport-timeout] ${label} timed out after ${timeoutMs}ms`, error);
      return new RequestTimeoutError(label, timeoutMs);
    },
    dispose: () => {
      if (timer !== undefined) clearTimeout(timer);
      upstream?.removeEventListener('abort', abortFromUpstream);
    },
  };
};

// Bounds everything `request` awaits — read the body inside it, or a stalled body escapes the bound.
export const withAbortTimeout = async <T>(
  label: string,
  timeoutMs: number,
  request: (signal: AbortSignal) => Promise<T>,
  upstream?: AbortSignal | null,
): Promise<T> => {
  const timer = createAbortTimer(label, timeoutMs, upstream);
  try {
    return await request(timer.signal);
  } catch (error) {
    throw timer.settleError(error);
  } finally {
    timer.dispose();
  }
};
